import {
  CardMessage,
  CLOUD_SECURITY_FEATURE,
  EMAIL_SECURITY_FEATURE,
  featureToId,
  GzLandingPageCompanyType,
  IntegrationFeature,
  LandingPageData,
  MOBILE_SECURITY_FEATURE,
  TEMPLATE_AREAS,
} from './types';
import { SsoIntegrationWidgetCard } from '../landingPageTemplate/widgets/ssoIntegration/types';
import { LPWidgetConfig } from '../landingPageTemplate/types';
import { translateFn } from '../utils/translator.helper';
import { AVAILABLE_RIGHTS } from '../accounts/common/accountRoleAndPrivileges/type';

export const repeatArea = (area: string, n: number) => {
  return `${area} `.repeat(n).trim().split(' ');
};

export const COMMON_TEMPLATE_AREAS = [
  [...repeatArea(TEMPLATE_AREAS.HEADER, 4), ...repeatArea(TEMPLATE_AREAS.HEADER_TOP, 2)],
  [...repeatArea(TEMPLATE_AREAS.HEADER, 4), ...repeatArea(TEMPLATE_AREAS.HEADER_BOTTOM, 2)],
  [...repeatArea(TEMPLATE_AREAS.NEWS, 6)],
];

export const getSsoWidgetsProps = (): Record<IntegrationFeature, SsoIntegrationWidgetCard> => {
  return {
    [CLOUD_SECURITY_FEATURE]: {
      id: 'cloud-security',
      icon: 'ds-icon-menu_cloud_sec',
      title: translateFn('Cloud Security'),
      description: translateFn(
        'Employ the power of GravityZone Cloud Security, a comprehensive solution ready to help you gain valuable insights, monitor and ensure the security of your cloud infrastructure.'
      ),
      url: 'https://www.bitdefender.com/business/support/en/77209-453798-cloud-security.html',
      provisionStatus: 0,
    },
    [MOBILE_SECURITY_FEATURE]: {
      id: 'mobile-security',
      icon: 'ds-icon-menu_mobile_security',
      title: translateFn('Mobile Security'),
      description: translateFn(
        'Employ our cloud-based solution that efficiently safeguards Android and iOS devices from all angles, keeping your data secure.'
      ),
      url: 'https://www.bitdefender.com/business/support/en/77209-300391-mobile-security.html',
      provisionStatus: 0,
    },
    [EMAIL_SECURITY_FEATURE]: {
      id: 'email-security',
      icon: 'cloud_email_security',
      title: translateFn('Email Security'),
      description: translateFn(
        'Enhance your email security with our cloud-driven email security gateway, providing protection against a wide range of email-centric threat vectors.'
      ),
      url: 'https://www.bitdefender.com/business/support/en/77209-294779-email-security.html',
      provisionStatus: 0,
    },
  };
};

export const getSsoSingleIntegrationProps = () => {
  return {
    [CLOUD_SECURITY_FEATURE]: {
      featureId: featureToId[CLOUD_SECURITY_FEATURE],
      icon: 'ds-icon-menu_cloud_sec',
      heading: translateFn('Harness the power of GravityZone Cloud Security to manage your cloud infrastructure'),
      description: translateFn(
        'Employ the power of GravityZone Cloud Security, a comprehensive solution ready to help you gain valuable insights, monitor and ensure the security of your cloud infrastructure.'
      ),
      supportUrl: 'https://www.bitdefender.com/business/support/en/77209-453798-cloud-security.html',
    },
    [MOBILE_SECURITY_FEATURE]: {
      featureId: featureToId[MOBILE_SECURITY_FEATURE],
      icon: 'ds-icon-menu_mobile_security',
      heading: translateFn('Experience comprehensive security for all your devices with our Mobile Security solution'),
      description: translateFn(
        'Employ our cloud-based solution that efficiently safeguards Android and iOS devices from all angles, keeping your data secure.'
      ),
      supportUrl: 'https://www.bitdefender.com/business/support/en/77209-300391-mobile-security.html',
    },
    [EMAIL_SECURITY_FEATURE]: {
      featureId: featureToId[EMAIL_SECURITY_FEATURE],
      icon: 'cloud_email_security',
      heading: 'Use a robust Email Security solution that protects you from email threats',
      description:
        'Enhance your email security with our cloud-driven email security gateway, providing protection against a wide range of email-centric threat vectors.',
      supportUrl: 'https://www.bitdefender.com/business/support/en/77209-294779-email-security.html',
    },
  };
};

export const getCommonWidgets = (landingPageData: LandingPageData): LPWidgetConfig[] => {
  return [
    {
      id: 'welcome',
      content: 'gz-landing-page-welcome',
      gridArea: TEMPLATE_AREAS.HEADER,
      props: {},
    },
    {
      id: 'news',
      content: 'gz-latest-news-carousel-widget',
      gridArea: TEMPLATE_AREAS.NEWS,
      props: {
        companyType: landingPageData.companyType,
      },
    },
  ];
};

export const getSpecificWidgets = (
  landingPageData: LandingPageData,
  messages: CardMessage[] | undefined,
  dynamicEventWidgetInfo: WidgetInfo | undefined,
  dynamicPromoWidgetInfo: WidgetInfo | undefined
): LPWidgetConfig[] => {
  const widgets: LPWidgetConfig[] = [];

  switch (landingPageData.companyType) {
    case GzLandingPageCompanyType.CUSTOMER:
      widgets.push(
        {
          id: 'installation packages',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_TOP,
          props: {
            featureTitle: translateFn('Installation Packages'),
            featureIcon: 'app/webcomponents/assets/images/landingPage/logo_bitdefender.svg',
            featureIconType: 'image',
            featureId: landingPageData.userRights[AVAILABLE_RIGHTS.MANAGE_INVENTORY]
              ? 'go-to-installation-packages'
              : '',
            compact: true,
            companyType: landingPageData.companyType,
          },
        },
        {
          id: 'early access',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_MIDDLE,
          props: {
            featureTitle: translateFn('Early Access'),
            featureIcon: 'app/webcomponents/assets/images/landingPage/logo_bitdefender.svg',
            featureIconType: 'image',
            featureId: landingPageData.userRights[AVAILABLE_RIGHTS.COMPANY_MANAGER] ? 'go-to-early-access' : '',
            compact: true,
            companyType: landingPageData.companyType,
          },
        },
        {
          id: 'help and support',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_BOTTOM,
          props: {
            featureTitle: translateFn('Help and Support'),
            featureIcon: 'ds-icon-trial_card_3',
            featureIconType: 'icon',
            featureId: 'go-to-help-and-support',
            iconSize: 32,
            compact: true,
            companyType: landingPageData.companyType,
          },
        },
        {
          id: 'masterclass',
          content: `gz-masterclass-widget`,
          gridArea: TEMPLATE_AREAS.MASTERCLASS,
          props: {
            companyType: landingPageData.companyType,
          },
        }
      );

      if (landingPageData.features.inProductTrials && !Application.Main.product.isPrivateCloud()) {
        widgets.push({
          id: 'product trials hub',
          content: `gz-product-trials-widget`,
          gridArea: TEMPLATE_AREAS.PRODUCT_TRIALS,
          props: {
            featureId: 'go-to-feature-product-trials',
            companyType: GzLandingPageCompanyType.CUSTOMER,
          },
        });
      }
      break;
    case GzLandingPageCompanyType.CSPM_STANDALONE:
      widgets.push(
        {
          id: 'help and support',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_BOTTOM,
          props: {
            featureTitle: translateFn('Help and Support'),
            featureIcon: 'ds-icon-trial_card_3',
            featureIconType: 'icon',
            featureId: 'go-to-help-and-support',
            iconSize: 32,
            compact: true,
            companyType: landingPageData.companyType,
          },
        },
        {
          id: 'masterclass',
          content: `gz-masterclass-widget`,
          gridArea: TEMPLATE_AREAS.MASTERCLASS,
          props: {
            companyType: landingPageData.companyType,
          },
        }
      );
      if (!Application.Main.product.isPrivateCloud()) {
        widgets.push({
          id: 'product trials',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_TOP,
          props: {
            featureTitle: translateFn('Products Hub'),
            featureIcon: 'ds-icon-product_trials_hub',
            featureIconType: 'icon',
            featureSummary: translateFn(
              'Access free, fully featured 30-day product trials! Unlock the power of GravityZone by seamlessly exploring new products in our trials section.'
            ),
            featureId: landingPageData.features.inProductTrials ? 'go-to-feature-product-trials' : '',
            companyType: landingPageData.companyType,
          },
        });
      }
      break;
    case GzLandingPageCompanyType.MSP:
      if (messages && messages.length) {
        const randomWidgetIndex = Math.floor(Math.random() * messages.length);
        widgets.push({
          id: 'important message',
          gridArea: TEMPLATE_AREAS.HEADER_TOP,
          isDynamic: true,
          dynamicContent: messages[randomWidgetIndex].message,
          props: {
            companyName: landingPageData.companyName,
            companyHash: landingPageData.companyHash,
            companyType: landingPageData.companyType,
          },
        });
      } else {
        widgets.push({
          id: 'help and support',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_TOP,
          props: {
            featureTitle: translateFn('Help and Support'),
            featureId: 'go-to-help-and-support',
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonColor: 'secondary',
            actionButtonType: 'outline',
            actionButtonText: translateFn('Get support'),
            featureSummary: translateFn(
              'Need assistance? Explore guides, resources, trainings, and FAQs, or connect with us through feedback and support forms.'
            ),
          },
        });
      }

      widgets.push(
        {
          id: 'product trials hub',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.HEADER_BOTTOM,
          props: {
            featureTitle: translateFn('MSP {OPEN_TAG} Free {CLOSE_TAG} Product Trials', {
              OPEN_TAG: '<span style="color:#FF0000;">',
              CLOSE_TAG: '</span>',
            }),
            featureId: 'go-to-feature-msp-product-trials',
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonColor: 'secondary',
            actionButtonType: 'outline',
            actionButtonText: translateFn('Browse eligible companies'),
            actionButtonTooltip: !landingPageData.features.inProductTrials
              ? translateFn(
                  'Available to Partner companies with a Monthly Subscription, Endpoint Security, and A la Carte licensing model, and to users who also have Manage Companies rights.'
                )
              : '',
            actionButtonDisabled: !landingPageData.features.inProductTrials,
            featureSummary: translateFn(
              'Unlock advanced MSP capabilities with free detection and response trials, offering up to 60 days of premium protection and 24/7 security monitoring.'
            ),
          },
        },
        {
          id: 'masterclass',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.BODY_LEFT_TOP,
          props: {
            featureTitle: translateFn('Masterclass'),
            featureId: 'go-to-trainings',
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonColor: 'secondary',
            actionButtonType: 'outline',
            actionButtonText: translateFn('Browse webinars'),
            featureSummary: translateFn(
              'Master Bitdefender products with our free live Masterclass webinars, led by expert cybersecurity professionals.'
            ),
          },
        },
        {
          id: 'event webinars',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.BODY_RIGHT_TOP,
          props: {
            featureTitle: dynamicEventWidgetInfo?.title,
            featureId: 'go-to-msp-events',
            featureBodyImage: dynamicEventWidgetInfo?.image,
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonText: dynamicEventWidgetInfo?.buttonName,
            featureSummary: dynamicEventWidgetInfo?.description,
          },
        },
        {
          id: 'early access',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.BODY_LEFT_BOTTOM,
          props: {
            featureTitle: translateFn('Early Access'),
            featureId: landingPageData.userRights[AVAILABLE_RIGHTS.COMPANY_MANAGER] ? 'go-to-early-access' : '',
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonColor: 'secondary',
            actionButtonType: 'outline',
            actionButtonText: translateFn('View programs'),
            featureSummary: translateFn(
              'Explore new features or products before their official release by enrolling in the available early access programs.'
            ),
          },
        },
        {
          id: 'msp promo',
          content: `gz-go-to-feature-widget`,
          gridArea: TEMPLATE_AREAS.BODY_RIGHT_BOTTOM,
          props: {
            featureTitle: dynamicPromoWidgetInfo?.title,
            featureId: 'go-to-msp-promo',
            featureBodyImage: dynamicPromoWidgetInfo?.image,
            compact: false,
            companyType: landingPageData.companyType,
            actionButtonText: dynamicPromoWidgetInfo?.buttonName,
            featureSummary: dynamicPromoWidgetInfo?.description,
          },
        }
      );
      break;
    default:
      break;
  }

  return widgets;
};

export const getLandingPageReference = (companyType: GzLandingPageCompanyType): string => {
  switch (companyType) {
    case GzLandingPageCompanyType.CSPM_STANDALONE:
      return 'landing-page-for-cspm-standalone';
    case GzLandingPageCompanyType.CUSTOMER:
      return 'landing-page-for-customer';
    case GzLandingPageCompanyType.MSP:
      return 'landing-page-for-msp';
    case GzLandingPageCompanyType.PARTNER:
      return 'landing-page-for-partner';
    default:
      return '';
  }
};
